import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  Input,
  Select,
  Space,
  Spin,
  Switch,
  Table,
  Tag,
  Tooltip,
} from 'antd';
import { ReloadOutlined, SyncOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import axios from 'axios';

const API = `${process.env.REACT_APP_BASE_URL}/api/v1/dashboard/admin/quickgift`;

const STATUS_META = {
  available: { color: 'green', label: 'Available' },
  expired: { color: 'gold', label: 'Expired' },
  unavailable: { color: 'red', label: 'Not available' },
  removed: { color: 'default', label: 'Removed' },
  failed: { color: 'blue', label: 'Sync error' },
};

const POLL_INTERVAL_MS = 8000;

export default function GiftCards() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [products, setProducts] = useState([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 50, total: 0 });
  const [statusFilter, setStatusFilter] = useState();
  const [search, setSearch] = useState('');
  const [lastRun, setLastRun] = useState(null);
  const pollTimer = useRef(null);

  const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`,
  });

  const fetchProducts = useCallback(
    async (page = 1, status = statusFilter, query = search) => {
      setLoading(true);
      try {
        const response = await axios.get(`${API}/products`, {
          headers: authHeaders(),
          params: {
            page,
            per_page: pagination.pageSize,
            status: status || undefined,
            search: query || undefined,
          },
        });
        const payload = response.data?.data;
        setProducts(payload?.data || []);
        setPagination((prev) => ({
          ...prev,
          current: payload?.current_page || 1,
          total: payload?.total || 0,
        }));
      } catch (error) {
        console.error('Error fetching gift cards:', error);
        toast.error('Failed to fetch gift cards');
      } finally {
        setLoading(false);
      }
    },
    [statusFilter, search, pagination.pageSize]
  );

  const stopPolling = () => {
    if (pollTimer.current) {
      clearTimeout(pollTimer.current);
      pollTimer.current = null;
    }
  };

  const checkSyncStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/sync-status`, { headers: authHeaders() });
      const run = response.data?.data;
      setLastRun(run);

      if (!run || run.status === 'success' || run.status === 'failed') {
        stopPolling();
        setSyncing(false);

        if (run?.status === 'success') {
          const stats = run.stats || {};
          toast.success(
            `Sync complete — available: ${stats.available_count ?? 0}, ` +
              `expired: ${stats.expired_count ?? 0}, ` +
              `unavailable: ${stats.unavailable_count ?? 0}, ` +
              `removed: ${stats.removed_count ?? 0}, ` +
              `failed: ${stats.failed_count ?? 0}`
          );
          fetchProducts(pagination.current);
        } else if (run?.status === 'failed') {
          toast.error(`Gift card sync failed: ${run.error || 'unknown error'}`);
        }
        return;
      }

      pollTimer.current = setTimeout(checkSyncStatus, POLL_INTERVAL_MS);
    } catch (error) {
      console.error('Error checking sync status:', error);
      stopPolling();
      setSyncing(false);
      toast.error('Could not check sync status');
    }
  }, [fetchProducts, pagination.current]);

  const startSync = async () => {
    setSyncing(true);
    try {
      const response = await axios.post(`${API}/sync`, {}, { headers: authHeaders() });
      setLastRun(response.data?.data || null);
      toast.info('Gift card sync started');
    } catch (error) {
      if (error.response?.status === 409) {
        toast.info('A sync is already in progress');
      } else {
        console.error('Error starting sync:', error);
        toast.error('Failed to start sync');
        setSyncing(false);
        return;
      }
    }
    pollTimer.current = setTimeout(checkSyncStatus, POLL_INTERVAL_MS);
  };

  const toggleActive = async (sku, checked) => {
    try {
      const response = await axios.patch(
        `${API}/products/${sku}/status`,
        { active: checked },
        { headers: authHeaders() }
      );
      const data = response.data?.data;
      toast.success(`${sku} ${checked ? 'activated' : 'deactivated'}`);
      if (data && checked && data.sync_status !== 'available' && !data.visible_to_users) {
        toast.warning('Upstream status is not "available" — this card stays hidden from users');
      }
      setProducts((prev) =>
        prev.map((p) => (p.sku === sku ? { ...p, active: checked } : p))
      );
    } catch (error) {
      console.error('Error updating gift card status:', error);
      toast.error('Failed to update gift card status');
    }
  };

  useEffect(() => {
    fetchProducts(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  useEffect(() => () => stopPolling(), []);

  const columns = [
    {
      title: t('image'),
      key: 'image',
      width: 70,
      render: (_, record) =>
        record.small_image || record.glogo ? (
          <img
            src={record.small_image || record.glogo}
            alt={record.name}
            style={{ width: 40, height: 40, objectFit: 'contain' }}
          />
        ) : (
          '—'
        ),
    },
    { title: t('name'), dataIndex: 'name', key: 'name' },
    { title: 'SKU', dataIndex: 'sku', key: 'sku' },
    {
      title: 'Sync Status',
      dataIndex: 'sync_status',
      key: 'sync_status',
      render: (status, record) => {
        const meta = STATUS_META[status] || { color: 'default', label: status || '—' };
        const tag = <Tag color={meta.color}>{meta.label}</Tag>;
        return record.sync_error ? (
          <Tooltip title={record.sync_error}>{tag}</Tooltip>
        ) : (
          tag
        );
      },
    },
    {
      title: t('price'),
      key: 'price',
      render: (_, record) =>
        record.price ? (
          `${record.price.minPrice} – ${record.price.maxPrice}`
        ) : (
          <Tag color='red'>no price</Tag>
        ),
    },
    {
      title: 'Last Synced',
      dataIndex: 'last_synced_at',
      key: 'last_synced_at',
      render: (value) => (value ? new Date(value).toLocaleString() : '—'),
    },
    {
      title: t('active'),
      dataIndex: 'active',
      key: 'active',
      render: (active, record) => (
        <Switch
          checked={!!active}
          onChange={(checked) => toggleActive(record.sku, checked)}
        />
      ),
    },
  ];

  return (
    <Card
      title='Gift Cards'
      extra={
        <Space>
          {lastRun?.status && (
            <Badge
              status={
                lastRun.status === 'success'
                  ? 'success'
                  : lastRun.status === 'failed'
                  ? 'error'
                  : 'processing'
              }
              text={`Last sync: ${lastRun.status}`}
            />
          )}
          <Button
            type='primary'
            icon={<SyncOutlined spin={syncing} />}
            onClick={startSync}
            disabled={syncing}
          >
            {syncing ? 'Syncing…' : 'Sync Gift Cards'}
          </Button>
        </Space>
      }
    >
      {syncing && (
        <div style={{ marginBottom: 16 }}>
          <Spin size='small' /> Sync in progress — this takes a few minutes…
        </div>
      )}

      <Space style={{ marginBottom: 16 }}>
        <Select
          allowClear
          placeholder='Filter by status'
          style={{ width: 180 }}
          onChange={(value) => setStatusFilter(value)}
          options={Object.entries(STATUS_META).map(([value, meta]) => ({
            value,
            label: meta.label,
          }))}
        />
        <Input.Search
          placeholder='Search by name or SKU'
          allowClear
          style={{ width: 260 }}
          onSearch={(value) => {
            setSearch(value);
            fetchProducts(1, statusFilter, value);
          }}
        />
        <Button icon={<ReloadOutlined />} onClick={() => fetchProducts(pagination.current)} />
      </Space>

      <Table
        rowKey='sku'
        loading={loading}
        columns={columns}
        dataSource={products}
        pagination={{
          ...pagination,
          showSizeChanger: false,
          showTotal: (total) => `${total} cards`,
        }}
        onChange={(pager) => fetchProducts(pager.current)}
      />
    </Card>
  );
}
